// src/pages/relatorios/PessoasPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Form, InputGroup, Row, Col, Spinner, Alert, Pagination } from 'react-bootstrap';
import { FaSearch, FaPrint } from 'react-icons/fa';
import useSortable from '../../hooks/useSortable';
import SortableTh from '../../components/SortableTh';
import api from '../../service/api';

interface Pessoa {
  PES_CODIGO: number;
  PES_NOME: string;
  PES_ENDERECO: string;
  PES_FONE: string;
  PES_CPF: string;
  PES_RG: string;
}

const ITEMS_PER_PAGE = 15;

const RelPessoasPage: React.FC = () => {
  const [pessoas, setPessoas] = useState<Pessoa[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const fetchPessoas = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/pessoas');
      setPessoas(res.data);
    } catch {
      setError('Não foi possível carregar os dados.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPessoas(); }, [fetchPessoas]);

  const filtered = pessoas.filter(p =>
    p.PES_NOME?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.PES_CPF?.includes(searchTerm)
  );
  const { sortedData: sortedPessoas, sortKey, sortDir, handleSort } = useSortable<Pessoa>(filtered, 'PES_NOME');
  const paginated = sortedPessoas.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Relatório de Pessoas</h1>
        <Button variant="outline-secondary" onClick={() => window.print()}>
          {FaPrint({ className: "me-2" })} Imprimir
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="shadow-sm">
        <Card.Body>
          <Row className="mb-3">
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>{FaSearch({})}</InputGroup.Text>
                <Form.Control
                  placeholder="Buscar por nome ou CPF..."
                  value={searchTerm}
                  onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
              </InputGroup>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end align-items-center">
              <span className="text-muted">Total: {filtered.length} pessoas</span>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : filtered.length === 0 ? (
            <Alert variant="info">Nenhuma pessoa encontrada.</Alert>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover size="sm">
                  <thead className="table-light">
                    <tr>
                      <SortableTh<Pessoa> label="Código" sortKey="PES_CODIGO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Pessoa> label="Nome" sortKey="PES_NOME" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Pessoa> label="CPF" sortKey="PES_CPF" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Pessoa> label="RG" sortKey="PES_RG" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Pessoa> label="Endereço" sortKey="PES_ENDERECO" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                      <SortableTh<Pessoa> label="Fone" sortKey="PES_FONE" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map(p => (
                      <tr key={p.PES_CODIGO}>
                        <td>{p.PES_CODIGO}</td>
                        <td>{p.PES_NOME}</td>
                        <td>{p.PES_CPF}</td>
                        <td>{p.PES_RG}</td>
                        <td>{p.PES_ENDERECO}</td>
                        <td>{p.PES_FONE}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                  <Pagination>
                    <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                    <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(p - 1, 1))} disabled={currentPage === 1} />
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                      .map((p, idx, arr) => (
                        <React.Fragment key={p}>
                          {arr[idx - 1] && p - arr[idx - 1] > 1 && <Pagination.Ellipsis disabled />}
                          <Pagination.Item active={p === currentPage} onClick={() => setCurrentPage(p)}>{p}</Pagination.Item>
                        </React.Fragment>
                      ))}
                    <Pagination.Next onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))} disabled={currentPage === totalPages} />
                    <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

export default RelPessoasPage;
